<template slot="main">
  <div class="context">
    <div v-if="noStats" class="text-center">
      <schema-ScoreSchema class="mb-3"></schema-ScoreSchema>
      <img src="../../../../assets/img/smiley_unhappy.png" style="height: 150px; width: 150px" class="mx-auto"/>
      <schema-NpsSchema class="mb-3"></schema-NpsSchema>
      <img src="../../../../assets/img/smiley_unhappy.png" style="height: 150px; width: 150px" class="mx-auto"/>
      <p class="text-xl">No statistics available</p>
      <p class="text-xl">The statistics will be available as soon as at least one user has answered the survey!</p>
      </div>
      <template v-else>
        <schema-ScoreSchema v-if="stats" class="mb-3"></schema-ScoreSchema>
        <canvas v-if="stats" id="chartBar" style="max-width: 650px; max-height: 500px;"></canvas>
        <schema-NpsSchema v-if="stats"></schema-NpsSchema>
        <canvas v-if="stats" id="lineNps" style="max-width: 650px; max-height: 500px;" ></canvas>
      </template>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Chart } from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'

export default {
  name: 'SurveyNPS',

  props: {
    id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loaded: false,
      chart: null,
      stats: [],
      noStats: false
    }
  },
  computed: {
    ...mapGetters('account', {
      site: 'currentSite',
      lang: 'defaultLang'
    }),
    ...mapGetters('surveys', {
      getById: 'getById',
      getStats: 'getStatsById'
    })
  },

  methods: {
    ...mapActions('surveys', ['loadSurveys', 'loadSurveyStats']),

    init() {
      if (this.getStats(this.id)) {
        this.loaded = true
        this.stats = this.getStats(this.id)
        this.drawBarChart()
        return
      }
      const startDate = new Date()
      startDate.setMonth(startDate.getMonth() - 12)
      const endDate = new Date()
      this.loadSurveyStats({ surveyId: this.id, startDate, endDate }).then(stats => {
        this.loaded = true

        if (!stats || stats.length === 0) {
          this.noStats = true
          return
        }

        stats.sort((a, b) => {
          const dateA = new Date(a.year, a.month - 1)
          const dateB = new Date(b.year, b.month - 1)
          return dateA - dateB
        })

        this.stats = stats

        this.drawBarChart()
        this.drawLineChart()
      })
    },

    drawBarChart() {
      const labels = this.stats.map(stat => {
        const date = new Date(stat.year, stat.month - 1)
        return date.toLocaleDateString(this.lang, { month: 'short', year: 'numeric' })
      })

      const ctx = document.getElementById('chartBar')

      new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'detractors',
              data: this.stats.map(stat => stat.detractorsCount),
              backgroundColor: 'rgb(211, 26, 26, 0.5)',
              borderColor: 'rgba(211, 26, 26, 1)',
              borderWidth: 0,
              stack: 'stack 0'
            },
            {
              label: 'passives',
              data: this.stats.map(stat => stat.passivesCount),
              backgroundColor: 'rgb(255, 158, 13, 0.5)',
              borderColor: 'rgba(255, 158, 13, 1)',
              borderWidth: 0,
              stack: 'stack 0'
            },
            {
              label: 'promoters',
              data: this.stats.map(stat => stat.promotersCount),
              backgroundColor: 'rgb(49, 115, 18, 0.5)',
              borderColor: 'rgba(49, 115, 18, 1)',
              borderWidth: 0,
              stack: 'stack 0'
            }
          ]
        },
        options: {
          plugins: {
            legend: false,
            datalabels: {
              display: true,
              align: 'center',
              anchor: 'center',
              color: 'black',
              formatter: (value, context) => {
                const percent = Math.round((value / this.stats[context.dataIndex].totalResponses) * 100)
                if (percent === 0) {
                  return ''
                }
                return `${value} (${percent}%)`
              },
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              suggestedMax: 5,
              ticks: {
                stepSize: 1
              }
            }
          },
        },
        plugins: [ChartDataLabels]
      })
    },
    drawLineChart() {
      const ctx = document.getElementById('lineNps')

      const labels = this.stats.map(stat => {
        const date = new Date(stat.year, stat.month - 1)
        return date.toLocaleDateString(this.lang, { month: 'short', year: 'numeric' })
      })

      const nps = this.stats.map(stat => stat.npsScore)

      const backgroundColors = nps.map(n => {
        if (n < 0) {
          return 'rgba(255, 0, 0, 0.2)'
        }
        if (n > 0) {
          return 'rgba(0, 255, 0, 0.2)'
        }
        return 'rgba(255, 255, 0, 0.2)'
      })
      const borderColors = nps.map(n => {
        if (n < 0) {
          return 'rgba(255, 0, 0, 1)'
        }
        if (n > 0) {
          return 'rgba(0, 255, 0, 1)'
        }
        return 'rgba(255, 255, 0, 1)'
      })

      new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'NPS',
              data: nps,
              borderColor: 'rgba(0, 0, 0, 1)',
              borderWidth: 1,
              fill: false,
              pointBorderWidth: 1,
              pointBorderColor: borderColors,
              pointBackgroundColor: backgroundColors,
              pointRadius: 10,
              lineTension: 0,
              pointHoverRadius: 11,
            }
          ]
        },
        options: {
          plugins: {
            legend: false,
            datalabels: {
              display: false,
              align: 'top',
              formatter: (value, context) => {
                return value > 0 ? `+${value}` : value
              }
            }
          },
          title: {
            display: true,
            text: 'Net promoter score'
          },
          tooltips: {
            enabled: false,
          },
          scales: {
            x: {
              offset: true,
            },
            y: {
              beginAtZero: true,
              min: -100,
              max: 110,
              ticks: {
                stepSize: 10,
              }
            }
          }
        },
        // plugins: [ChartDataLabels]
      })
    }
  },
  mounted() {
    this.init()
  }
}
</script>

<style>

.context {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.chart-container {
  display: flex;
  flex-direction: column;
  /* flex-wrap: wrap; */
  justify-content: center;
  gap: 20px;
  /* max-width: 100%; */
}

.chart-container canvas {
  max-width: 600px;
}

.containerImage {
  display: inline-flex;
  padding: 10px;
  align-items: flex-start;
  gap: 10px;
  background: #DFDFDF;
  margin-top: 25px;
}

</style>



// WEBPACK FOOTER //
// src/components/surveys/ui/stats/nps.vue